// Selection logic for which festival areas the READ-ONLY live-tracking maps
// (producer LiveTracking + crew CrewLiveTracking) may draw.
//
// Pulled out as a pure function so the rule is unit-testable and shared by
// both maps — the two used to keep byte-identical copies of it inline.
//
// Why an allowlist and not `area_type !== "stage_zone"`: the tracking maps
// must NEVER render the `festival_boundary` — that polygon belongs only to
// the zone editor (LiveTrackingSetup). The old blocklist let the boundary
// through whenever the shared React Query cache (`["festival-areas", id]`)
// had been filled with a superset by another reader (Analytics uses
// `select("*")` with no area_type filter). That was the intermittent
// "festival zone shows up in the view-only map" bug. An allowlist makes the
// render safe regardless of what ends up in the cache, now or in future.

/**
 * The only area types a read-only live-tracking map is allowed to show.
 * Deliberately excludes `festival_boundary` (editor-only) and any future
 * area type until it's explicitly opted in here.
 */
export const VISIBLE_TRACKING_AREA_TYPES = ["stage_zone", "media_office", "crew_catering"] as const;

const LOCATION_AREA_TYPES = ["media_office", "crew_catering"] as const;

type TrackingAreaLike = {
  area_type: string;
  day_index?: number | null;
};

/**
 * Pick the festival areas to render on a read-only tracking map for a given
 * day:
 *   - stage zones for `dayIndex`, falling back to day 0 when the selected
 *     day has none (multi-day festivals reuse the day-0 layout);
 *   - the always-on location zones (media office, crew catering), regardless
 *     of day.
 *
 * Everything else — most importantly `festival_boundary` — is dropped.
 */
export function selectVisibleTrackingAreas<T extends TrackingAreaLike>(
  areas: T[] | null | undefined,
  dayIndex: number,
): T[] {
  if (!areas || areas.length === 0) return [];

  const stageZones = areas.filter((a) => a.area_type === "stage_zone");
  const currentDayStageZones = stageZones.filter((a) => a.day_index === dayIndex);
  const day0StageZones = stageZones.filter((a) => a.day_index === 0);
  const activeStageZones = currentDayStageZones.length > 0 ? currentDayStageZones : day0StageZones;

  const locationZones = areas.filter((a) =>
    (LOCATION_AREA_TYPES as readonly string[]).includes(a.area_type),
  );

  return [...activeStageZones, ...locationZones];
}
